// Lógica pura de la selección múltiple de tablas de guías (rango con Shift y
// arrastre). Vive acá y no dentro del componente para poder testearla: la
// pantalla está detrás de login y no hay forma de ejercitarla de otro modo.

interface Identifiable {
  shipmentHeaderID: string
}

const byShipmentHeaderID = (t: any): string => t.shipmentHeaderID

/**
 * SOBRECARGAS, no un parámetro con default.
 *
 * La tentación era `key: (t: T) => string = (t: any) => t.shipmentHeaderID` para
 * que sirviera con cualquier tipo. Pero eso cambia un error de compilación por un
 * fallo SILENCIOSO: un tipo sin `shipmentHeaderID` devuelve `undefined` en todas
 * las claves, y entonces `toggleSelection` trata todas las filas como si fueran
 * la misma.
 *
 * Justo el tipo de bug que estas funciones vienen a cerrar. Con dos sobrecargas,
 * los llamadores que ya existen (que pasan objetos con `shipmentHeaderID`) siguen
 * compilando sin tocarlos, y usar un tipo nuevo sin pasar extractor vuelve a ser
 * un error de tipos.
 */

/**
 * Marca o desmarca un lote sobre la selección actual, comparando por clave y no
 * por referencia de objeto.
 *
 * Al marcar, los elementos del lote se reinsertan al final: así un item que ya
 * estaba seleccionado no queda duplicado.
 */
export function mergeSelection<T extends Identifiable>(
  current: T[],
  targets: T[],
  selected: boolean
): T[]
export function mergeSelection<T>(
  current: T[],
  targets: T[],
  selected: boolean,
  key: (t: T) => string
): T[]
export function mergeSelection<T>(
  current: T[],
  targets: T[],
  selected: boolean,
  key: (t: T) => string = byShipmentHeaderID
): T[] {
  const ids = new Set(targets.map(key))
  const rest = current.filter((s) => !ids.has(key(s)))

  return selected ? [...rest, ...targets] : rest
}

/** Alterna un solo item, comparando por clave. */
export function toggleSelection<T extends Identifiable>(
  current: T[],
  target: T
): T[]
export function toggleSelection<T>(
  current: T[],
  target: T,
  key: (t: T) => string
): T[]
export function toggleSelection<T>(
  current: T[],
  target: T,
  key: (t: T) => string = byShipmentHeaderID
): T[] {
  const targetKey = key(target)

  return current.some((s) => key(s) === targetKey)
    ? current.filter((s) => key(s) !== targetKey)
    : [...current, target]
}

/**
 * Tramo contiguo entre dos índices, en cualquier orden (el usuario puede armar
 * el rango hacia abajo o hacia arriba). Ambos extremos quedan incluidos.
 */
export const rangeBetween = <T>(items: T[], from: number, to: number): T[] => {
  const [start, end] = from < to ? [from, to] : [to, from]

  return items.slice(start, end + 1)
}

// ─── Atajos de selección del Review de carga masiva ─────────────────────────
//
// Un archivo de aliado trae cientos de guías (el de referencia: 403, con 98 en
// Maracaibo y 65 en Caracas). Marcarlas de a una no es una molestia, es una tarea
// que no se puede hacer. El checkbox global cubre "todas"; estos chips cubren los
// subconjuntos por los que el operador realmente piensa la corrección.

/**
 * Fila mínima que necesitan los chips. No se pide el `ShipmentGroup` completo a
 * propósito: así la lógica se testea sin construir el objeto entero.
 */
export interface SelectableRow {
  id: string
  destinationCity?: string
  hasError: boolean
  /**
   * La fila falla POR EL TELÉFONO. Es su propio chip porque la corrección es
   * distinta a la del resto: un archivo de un aliado colombiano trae 150 filas
   * con el mismo problema y se arreglan de una, eligiendo el país en la barra
   * de acciones.
   */
  hasPhoneError?: boolean
}

export interface SelectionChip {
  key: string
  /** Texto del chip, ya listo para mostrar. */
  label: string
  count: number
  /** IDs que abarca el chip. Al pulsarlo la selección pasa a ser exactamente esto. */
  ids: string[]
}

export const CHIP_ERRORS = 'errors'
export const CHIP_PHONE_ERRORS = 'phone'
export const cityChipKey = (city: string): string => `city:${city}`

/**
 * Chips de atajo para la selección, ordenados por utilidad.
 *
 * Deliberadamente NO emite un chip "todas": esa función ya la cumple el checkbox
 * global del encabezado, y dos controles que hacen lo mismo son ruido.
 *
 * Omite el chip de errores cuando TODAS las filas fallan (sería idéntico al
 * checkbox global) y los chips de ciudad cuando hay una sola ciudad (ídem).
 *
 * @param maxCities tope de chips de ciudad, las más numerosas primero.
 */
export const buildSelectionChips = (
  rows: SelectableRow[],
  maxCities = 4
): SelectionChip[] => {
  if (rows.length === 0) return []

  const chips: SelectionChip[] = []

  const withError = rows.filter((r) => r.hasError)
  if (withError.length > 0 && withError.length < rows.length) {
    chips.push({
      key: CHIP_ERRORS,
      label: 'con error',
      count: withError.length,
      ids: withError.map((r) => r.id),
    })
  }

  // Chip propio para el teléfono: se omite si coincide EXACTAMENTE con el de
  // errores (mismo conjunto de filas), porque entonces sería el mismo botón
  // repetido con otro nombre.
  const withPhoneError = rows.filter((r) => r.hasPhoneError)
  if (
    withPhoneError.length > 0 &&
    withPhoneError.length !== withError.length
  ) {
    chips.push({
      key: CHIP_PHONE_ERRORS,
      label: 'con error de teléfono',
      count: withPhoneError.length,
      ids: withPhoneError.map((r) => r.id),
    })
  }

  const byCity = new Map<string, string[]>()
  rows.forEach((r) => {
    const city = (r.destinationCity || '').trim()
    if (!city) return
    const ids = byCity.get(city)
    if (ids) ids.push(r.id)
    else byCity.set(city, [r.id])
  })

  if (byCity.size > 1) {
    Array.from(byCity.entries())
      .sort((a, b) => b[1].length - a[1].length || a[0].localeCompare(b[0]))
      .slice(0, maxCities)
      .forEach(([city, ids]) =>
        chips.push({
          key: cityChipKey(city),
          label: city,
          count: ids.length,
          ids,
        })
      )
  }

  return chips
}
